import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useToast } from '../../contexts/ToastContext.jsx';
import { useTheme } from '../../contexts/ThemeContext.jsx';
import { ApiError } from '../../utils/apiClient.js';
import { parseApiError } from '../../utils/consolidatedErrorHandling.js';
import { apiRequest } from '../../utils/apiClient.js';
import LoadingSpinner from '../LoadingSpinner.jsx';

/**
 * Component for assigning phone numbers to users and parrots
 */
const NumberAssignment = ({
  isOpen = false,
  number = null,
  onClose = () => {},
  onNumberAssigned = () => {}
}) => {
  const { getToken } = useAuth();
  const { showToast } = useToast();
  const { theme } = useTheme();

  // State management
  const [users, setUsers] = useState([]);
  const [parrots, setParrots] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedParrotId, setSelectedParrotId] = useState('');
  const [loading, setLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [userParrots, setUserParrots] = useState([]);

  /**
   * Fetch users for assignment
   */
  const fetchUsers = useCallback(async () => {
    try {
      const token = await getToken();
      const response = await apiRequest('/api/admin/users?limit=1000&sortBy=first_name', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorMessage = await parseApiError(response);
        throw new ApiError(errorMessage, response.status, response);
      }

      const data = await response.json();
      setUsers(data.users || []);

    } catch (error) {
      console.error('Error fetching users:', error);
      showToast('Failed to load users', 'error');
    }
  }, [getToken, showToast]);

  /**
   * Fetch parrots for selected user
   */
  const fetchUserParrots = useCallback(async (userId) => {
    if (!userId) {
      setUserParrots([]);
      return;
    }

    try {
      const token = await getToken();
      const response = await apiRequest(`/api/admin/parrots?userId=${userId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorMessage = await parseApiError(response);
        throw new ApiError(errorMessage, response.status, response);
      }

      const data = await response.json();
      console.log('Fetched user parrots:', {
        userId,
        parrots: data.parrots?.map(p => ({ id: p.id, name: p.name })) || []
      });
      setUserParrots(data.parrots || []);

    } catch (error) {
      console.error('Error fetching user parrots:', error);
      setUserParrots([]);
    }
  }, [getToken]);

  /**
   * Handle user selection
   */
  const handleUserChange = (userId) => {
    setSelectedUserId(userId);
    setSelectedParrotId(''); // Reset parrot selection
    fetchUserParrots(userId);
  };

  /**
   * Handle assignment submission
   */
  const handleAssign = useCallback(async () => {
    if (!selectedUserId) {
      showToast('Please select a user', 'error');
      return;
    }

    // Debug logging
    console.log('Assignment attempt:', {
      selectedUserId,
      selectedParrotId,
      userParrots: userParrots.map(p => ({ id: p.id, name: p.name })),
      number: number?.id
    });

    // Validate parrot selection if provided
    if (selectedParrotId && !userParrots.find(p => String(p.id) === String(selectedParrotId))) {
      console.error('Parrot validation failed:', {
        selectedParrotId,
        selectedParrotIdType: typeof selectedParrotId,
        availableParrots: userParrots.map(p => ({ id: p.id, idType: typeof p.id, name: p.name }))
      });
      showToast('Selected parrot not found!', 'error');
      return;
    }

    try {
      setAssigning(true);

      const token = await getToken();
      const response = await apiRequest(`/api/admin/phone-numbers/${number.id}/assign`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          userId: selectedUserId,
          parrotId: selectedParrotId || null
        })
      });

      if (!response.ok) {
        const errorMessage = await parseApiError(response);
        throw new ApiError(errorMessage, response.status, response);
      }

      const data = await response.json();
      
      // Create updated number object for the callback
      const updatedNumber = {
        ...number,
        assigned_user_id: data.assignment.assignedUser.id,
        assigned_user_name: data.assignment.assignedUser.name,
        assigned_user_email: data.assignment.assignedUser.email,
        assigned_parrot_id: data.assignment.assignedParrot?.id || null,
        assigned_parrot_name: data.assignment.assignedParrot?.name || null,
        status: 'assigned'
      };
      
      onNumberAssigned(updatedNumber);
      onClose();

    } catch (error) {
      console.error('Error assigning number:', error);
      showToast(error.message || 'Failed to assign phone number', 'error');
    } finally {
      setAssigning(false);
    }
  }, [getToken, number, selectedUserId, selectedParrotId, onNumberAssigned, onClose, showToast]);

  /**
   * Initialize component when opened
   */
  useEffect(() => {
    if (isOpen && number) {
      setLoading(true);
      setSelectedUserId(number.assigned_user_id || '');
      setSelectedParrotId(number.assigned_parrot_id || '');
      
      fetchUsers().finally(() => {
        setLoading(false);
      });

      // If number is already assigned, fetch the user's parrots
      if (number.assigned_user_id) {
        fetchUserParrots(number.assigned_user_id);
      }
    }
  }, [isOpen, number, fetchUsers, fetchUserParrots]);

  if (!isOpen || !number) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className={`rounded-lg max-w-2xl w-full ${
        theme === 'dark' ? 'bg-gray-800' : 'bg-white'
      }`}>
        {/* Header */}
        <div className={`flex justify-between items-center p-6 border-b ${
          theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
        }`}>
          <h2 className={`text-xl font-semibold ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>
            {number.assigned_user_id ? 'Reassign' : 'Assign'} Phone Number
          </h2>
          <button
            onClick={onClose}
            className={`transition-colors ${
              theme === 'dark' 
                ? 'text-gray-400 hover:text-white' 
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <LoadingSpinner />
              <span className="ml-3 text-gray-400">Loading...</span>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Phone Number Info */}
              <div className={`rounded-lg p-4 ${
                theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100'
              }`}>
                <h3 className={`text-lg font-medium mb-2 ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>
                  {number.phone_number}
                </h3>
                <div className={`text-sm ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  Current assignment: {number.assigned_user_name || 'Unassigned'}
                  {number.assigned_parrot_name && ` → ${number.assigned_parrot_name}`}
                </div>
              </div>

              {/* User Selection */}
              <div>
                <label htmlFor="user-select" className={`block text-sm font-medium mb-2 ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Assign to User *
                </label>
                <select
                  id="user-select"
                  value={selectedUserId}
                  onChange={(e) => handleUserChange(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                    theme === 'dark'
                      ? 'bg-gray-700 border-gray-600 text-white'
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                  required
                >
                  <option value="">Select a user...</option>
                  {users.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.first_name} {user.last_name} ({user.email})
                    </option>
                  ))}
                </select>
              </div>

              {/* Parrot Selection */}
              <div>
                <label htmlFor="parrot-select" className={`block text-sm font-medium mb-2 ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Assign to Parrot (Optional)
                </label>
                <select
                  id="parrot-select"
                  value={selectedParrotId}
                  onChange={(e) => setSelectedParrotId(e.target.value)}
                  disabled={!selectedUserId}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:opacity-50 ${
                    theme === 'dark'
                      ? 'bg-gray-700 border-gray-600 text-white'
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                >
                  <option value="">No specific parrot</option>
                  {userParrots.map((parrot) => (
                    <option key={parrot.id} value={parrot.id}>
                      {parrot.name}
                    </option>
                  ))}
                </select>
                {!selectedUserId && (
                  <p className={`text-xs mt-1 ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                  }`}>
                    Select a user first to see their parrots
                  </p>
                )}
                {selectedUserId && userParrots.length === 0 && (
                  <p className={`text-xs mt-1 ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                  }`}>
                    This user has no parrots configured
                  </p>
                )}
              </div>

              {/* Assignment Info */}
              {selectedUserId && (
                <div className="bg-blue-900/20 border border-blue-500/50 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-blue-400 mb-2">
                    Assignment Summary
                  </h4>
                  <div className="text-sm text-gray-300 dark:text-gray-600">
                    <div>
                      Phone: <span className="text-white dark:text-white">{number.phone_number}</span>
                    </div>
                    <div>
                      User: <span className="text-white dark:text-white">
                        {users.find(u => u.id === selectedUserId)?.first_name} {users.find(u => u.id === selectedUserId)?.last_name}
                      </span>
                    </div>
                    {selectedParrotId && (
                      <div>
                        Parrot: <span className="text-white dark:text-white">
                          {userParrots.find(p => p.id === selectedParrotId)?.name}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 p-6 border-t border-gray-700">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors dark:text-white"
          >
            Cancel
          </button>
          <button
            onClick={handleAssign}
            disabled={!selectedUserId || assigning || loading}
            className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 flex items-center space-x-2 dark:text-white"
          >
            {assigning && <LoadingSpinner size="sm" />}
            <span>
              {assigning ? 'Assigning...' : (number.assigned_user_id ? 'Reassign' : 'Assign')}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default NumberAssignment;
